import { useState } from 'react';
import { IconPercentage } from '@tabler/icons-react';
import { useAuthStore } from '../../lib/auth.js';
import { ApiError } from '../../lib/api.js';
import { Button } from '../ui/Button.js';
import { SendModalShell } from '../send/SendModalShell.js';
import { centsToAudDisplay } from '../../lib/money.js';
import { bpToPercentDisplay } from '../../lib/percent.js';
import { editorLabel } from '../../lib/editor-label.js';
import { formatDate } from '../../lib/format.js';
import { CommissionTermsForm } from './CommissionTermsForm.js';
import { CourseOverridesSection } from './CourseOverridesSection.js';
import {
  basisLabel,
  buildProviderCommissionConfirm,
  commissionSummary,
  commissionVersionDetail,
  commissionVersionLabel,
  deductionLabel,
  formatCommissionDate,
  intentToRow,
  paymentRouteLabel,
  type SetProviderCommissionBody,
} from '../../lib/commission.js';
import {
  useProviderCommissionHistory,
  useSetProviderCommissionTerms,
  type CommissionOverviewCollege,
  type CommissionTerms,
} from '../../hooks/useCommission.js';

/** Local "are you sure?" dialog — mirrors the one in CourseOverridesSection (no shared component). */
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <SendModalShell open={open} onClose={onCancel} blocking={loading}>
      <div className="p-6">
        <h2 className="font-serif text-xl tracking-tight text-ink">{title}</h2>
        <p className="mt-2 text-sm text-muted">{body}</p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </SendModalShell>
  );
}

/**
 * One "Label: value" line. Renders NOTHING when unset — deliberate: for a deal,
 * an absent field IS the meaning ("no payment route was agreed"), so a dash
 * would imply a missing value rather than an unmade agreement.
 */
function Field({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function CurrentDealCard({ current }: { current: CommissionTerms | null }) {
  return (
    <div className="mt-5 rounded-lg border border-line bg-cream/40 px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-muted">Current deal</div>
      <div className="text-lg font-medium text-navy">{commissionSummary(current)}</div>

      {/* kind FIRST: an ended or inherited row still carries basis
          'whole_course' (the engine's CHECK forces it), so it must never render
          a basis, a route or a deduction. */}
      {current?.kind === 'terms' && (
        <dl className="mt-3 space-y-1 text-sm">
          <Field
            label="Rate"
            value={current.rateBp != null ? bpToPercentDisplay(current.rateBp) : null}
          />
          <Field
            label="Flat amount"
            value={
              current.flatAmountCents != null
                ? `${centsToAudDisplay(current.flatAmountCents)} per student`
                : null
            }
          />
          <Field label="Applies to" value={basisLabel(current.basis, current.basisMonths)} />
          <Field label="Calculated" value={deductionLabel(current.deduction)} />
          <Field label="Paid" value={paymentRouteLabel(current.paymentRoute)} />
        </dl>
      )}
      {current?.kind === 'terms' && current.studentVisaForcesDirect && (
        <p className="mt-2 text-sm text-ink">500-visa students: always paid direct</p>
      )}
      {current && (
        <div className="mt-2 text-xs text-muted">
          Effective {formatCommissionDate(current.validFrom)}
        </div>
      )}
      {current?.notes && <p className="mt-2 text-sm text-ink-soft">{current.notes}</p>}
    </div>
  );
}

/**
 * One college's commission deal (Brief 6b): the current deal in plain English,
 * the effective-dated editor, End deal, the version history, and the per-course
 * special deals below.
 *
 * Takes the overview row as a prop and never refetches the current values — the
 * PricingDetailPanel precedent. `current` arrived already resolved server-side
 * at `asOf`, so this panel does no date arithmetic of its own; it only fetches
 * history.
 */
export function CommissionDetailPanel({
  college,
  asOf,
}: {
  college: CommissionOverviewCollege;
  asOf: string;
}) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const history = useProviderCommissionHistory(college.providerId);
  const setTerms = useSetProviderCommissionTerms();
  // The pending payload IS the open state: save and End deal share one dialog.
  const [intent, setIntent] = useState<SetProviderCommissionBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const busy = setTerms.isPending;
  const rows = history.data?.history ?? [];
  // Server as-of, not the browser's today — commission may be future-dated.
  const currentVersionId = rows.find((v) => v.validFrom <= asOf)?.id ?? null;
  const inForce = college.current != null && college.current.kind !== 'none';

  const clearBanners = () => {
    setError(null);
    setSuccess(null);
  };

  async function commit() {
    if (!intent) return;
    clearBanners();
    try {
      await setTerms.mutateAsync({ providerId: college.providerId, body: intent });
      setSuccess(
        intent.kind === 'none'
          ? 'Deal ended.'
          : `Deal set to ${commissionSummary(intentToRow(intent))}.`,
      );
      setIntent(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      // Close so the banner is visible rather than behind the modal overlay.
      setIntent(null);
    }
  }

  return (
    <div className="surface p-5">
      <div className="flex items-start gap-2">
        <IconPercentage size={22} className="mt-0.5 shrink-0 text-gold" />
        <h2 className="font-serif text-xl tracking-tight text-ink">{college.providerName}</h2>
      </div>

      <CurrentDealCard current={college.current} />

      <CommissionTermsForm
        idPrefix="college"
        groupLabel="College commission deal"
        current={college.current}
        defaultValidFrom={asOf}
        busy={busy}
        submitLabel="Save deal"
        onDirty={clearBanners}
        onRequestSave={(body) => setIntent(body)}
      />

      {inForce && (
        <div className="mt-3">
          {/* Ends on the server's today. The brief asks only for "confirm → POST
              kind='none'"; reusing the editor's date field would couple ending a
              deal to a form the user may be part-way through typing. */}
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => setIntent({ kind: 'none', validFrom: asOf })}
          >
            End deal
          </Button>
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-3 rounded-lg border border-success/30 bg-success-bg px-3 py-2 text-sm text-success">
          {success}
        </div>
      )}

      <div className="mt-6">
        <h3 className="mb-2 font-serif text-base text-ink">Change history</h3>
        {history.isLoading && <p className="text-sm text-muted">Loading history…</p>}
        {history.error && <p className="text-sm text-danger">Couldn&apos;t load the history.</p>}
        {!history.isLoading && !history.error && rows.length === 0 && (
          <p className="text-sm text-muted">No deal has been set yet.</p>
        )}
        {!history.isLoading && !history.error && rows.length > 0 && (
          <ul className="divide-y divide-line">
            {rows.map((v) => (
              <li key={v.id} className="py-2">
                <div className="text-sm text-ink">
                  {commissionVersionLabel(v)}
                  {v.id === currentVersionId && (
                    <span className="badge ml-2 bg-success-bg text-success">Current</span>
                  )}
                </div>
                {commissionVersionDetail(v) && (
                  <div className="text-xs text-muted">{commissionVersionDetail(v)}</div>
                )}
                {v.notes && <div className="text-xs text-ink-soft">{v.notes}</div>}
                <div className="text-xs text-muted">
                  Effective {formatCommissionDate(v.validFrom)} · {editorLabel(v, currentUserId)} ·{' '}
                  {formatDate(v.createdAt)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CourseOverridesSection college={college} asOf={asOf} />

      <ConfirmDialog
        open={intent !== null}
        title={intent?.kind === 'none' ? 'End the college deal' : 'Set the college deal'}
        body={
          intent ? buildProviderCommissionConfirm(college.providerName, intent, college.current) : ''
        }
        confirmLabel={intent?.kind === 'none' ? 'End deal' : 'Set deal'}
        loading={busy}
        onConfirm={() => void commit()}
        onCancel={() => setIntent(null)}
      />
    </div>
  );
}
